import { z } from 'zod';
import { ExperienceType, SpaceType } from '../../../../enums/experience';
import { Experience } from '../../../../types/experience';
import { SpaceSchema } from './schemas/spaces.schema';

const BasePropertyAmenitiesSchema = z.object({
    details: z.object({
        bedrooms: z.number().min(1, { message: 'At least 1 bedroom required' }),
        beds: z.number().min(1, { message: 'At least 1 bed required' }),
        baths: z.number().min(1, { message: 'At least 1 bathroom required' }),
        maxGuests: z.number().min(1, { message: 'At least 1 guest required ' }),
        overnightGuests: z.number(),
        catering: z.boolean().default(false),
        alcohol: z.boolean().default(false),
        wifi: z.boolean().default(false),
        boardroom: z.boolean().default(false),
        tv: z.boolean().default(false),
        kitchen: z.boolean().default(false),
        parking: z.boolean().default(false),
        aircon: z.boolean().default(false),
        dedicatedWorkspace: z.boolean().default(false),
        pool: z.boolean().default(false),
        bbq: z.boolean().default(false),
        firepit: z.boolean().default(false),
        washingMachine: z.boolean().default(false),
        gamesRoom: z.boolean().default(false),
        spa: z.boolean().default(false),
        wineCellar: z.boolean().default(false),
        massages: z.boolean().default(false),
    }),
    spaces: z.array(SpaceSchema.and(z.object({ id: z.number().optional() }))),
});

export const PropertyAmenitiesSchema = BasePropertyAmenitiesSchema.superRefine(
    (val, ctx) => {
        const totalSleepingCapacity = val.spaces.reduce((acc, space) => {
            if (space.spaceType !== SpaceType.Bedroom) return acc;
            const cap = space.sleepingCapacity;
            return acc + (cap ?? 0);
        }, 0);
        const overnightGuests = val.details.overnightGuests;
        if (totalSleepingCapacity > overnightGuests) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    'Total bedroom sleeping capacity cannot exceed overnight guests.',
                path: ['details', 'overnightGuests'],
            });
        }
    },
);

export const PropertyPartialAmenitiesSchema =
    BasePropertyAmenitiesSchema.extend({
        details: BasePropertyAmenitiesSchema.shape.details
            .extend({
                bedrooms: z.number(),
                beds: z.number(),
                baths: z.number(),
                maxGuests: z.number(),
            })
            .partial(),
    });

const BaseCommercialAmenitiesSchema = z.object({
    details: z.object({
        desks: z.coerce.number(),
        privateOffices: z.coerce.number(),
        meetingRooms: z.coerce.number(),
        catering: z.boolean().default(false),
        wifi: z.boolean().default(false),
        boardroom: z.boolean().default(false),
        tv: z.boolean().default(false),
        kitchen: z.boolean().default(false),
        parking: z.boolean().default(false),
        aircon: z.boolean().default(false),
        dedicatedWorkspace: z.boolean().default(false),
        balcony: z.boolean().default(false),
        bbq: z.boolean().default(false),
        gym: z.boolean().default(false),
        diningOptions: z.boolean().default(false),
        petFriendly: z.boolean().default(false),
        showerRoom: z.boolean().default(false),
        bikeLock: z.boolean().default(false),
        personalStorage: z.boolean().default(false),
        childcare: z.boolean().default(false),
        maxGuests: z.coerce
            .number()
            .min(1, { message: 'At least 1 guest required ' }),
        meetingRoomMaxGuests: z.coerce
            .number()
            .min(1, { message: 'At least 1 guest required ' }),
    }),
    spaces: z.array(SpaceSchema.and(z.object({ id: z.number().optional() }))),
});

export const CommercialAmenitiesSchema =
    BaseCommercialAmenitiesSchema.superRefine((val, ctx) => {
        const offices = val.spaces.filter(
            (space) =>
                space.spaceType === SpaceType.PrivateOffice &&
                space.isAvailable,
        );

        const meetingRooms = val.spaces.filter(
            (space) =>
                space.spaceType === SpaceType.Boardroom && space.isAvailable,
        );
        if (offices.length !== val.details.privateOffices) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    'Number of configured offices does not match number of available offices.',
                path: ['spaces'],
            });
        }

        if (meetingRooms.length !== val.details.meetingRooms) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    'Number of configured meeting rooms does not match number of available meeting rooms.',
                path: ['spaces'],
            });
        }
    });

export const CommercialPartialAmenitiesSchema =
    BaseCommercialAmenitiesSchema.extend({
        details: BaseCommercialAmenitiesSchema.shape.details
            .extend({
                maxGuests: z.coerce.number(),
                meetingRoomMaxGuests: z.coerce.number(),
            })
            .partial(),
    });

const VehicleDetailsSchema = z.object({
    make: z.string().min(1, { message: 'Required' }),
    model: z.string().min(1, { message: 'Required' }),
    year: z
        .string()
        .optional()
        .or(z.literal(null))
        .transform((val) => (val ? val : null)),
    transmission: z.enum(['manual', 'automatic', 'cvt', 'electric'], {
        message: 'Required',
    }),
    heatedSeats: z.boolean().default(false),
    appleCarPlay: z.boolean().default(false),
    androidAuto: z.boolean().default(false),
    fuelType: z.enum(['petrol', 'diesel', 'electric', 'hybrid', 'cng', 'lpg'], {
        message: 'Required',
    }),
    rearVisionCamera: z.boolean().default(false),
    cruiseControl: z.boolean().default(false),
    touchscreenDisplay: z.boolean().default(false),
    bluetooth: z.boolean().default(false),
    blindSpotAlert: z.boolean().default(false),
    navigation: z.boolean().default(false),
    tyrePressureSensor: z.boolean().default(false),
    airbags: z.boolean().nullable().default(false),
});

export const VehicleCarAmenitiesSchema = z.object({
    details: VehicleDetailsSchema.extend({
        bodyType: z
            .enum(
                [
                    'bus',
                    'cabChassis',
                    'convertible',
                    'coupe',
                    'fastback',
                    'hatchback',
                    'lightTruck',
                    'limousine',
                    'minivan',
                    'sedan',
                    'sportsCar',
                    'stationWagon',
                    'suv',
                    'ute',
                    'van',
                ],
                { message: 'Required' },
            )
            // allow for init with wmpty string
            .or(z.literal(''))
            .refine((val) => val !== '', { message: 'Required' }),
        numberOfDoors: z.coerce.number().min(1, { message: 'Required' }),
        numberOfSeats: z.coerce.number().min(1, { message: 'Required' }),
        ac: z.boolean().nullable().default(false),
        keylessEntry: z.boolean().nullable().default(false),
        sunroof: z.boolean().nullable().default(false),
        laneAssist: z.boolean().nullable().default(false),
        autoPark: z.boolean().nullable().default(false),
        powerSeats: z.boolean().nullable().default(false),
        remoteStart: z.boolean().nullable().default(false),
        wirelessPhoneCharger: z.boolean().nullable().default(false),
        rearEntertainmentSystem: z.boolean().nullable().default(false),
    }),
});

export const VehicleMotorbikeAmenitiesSchema = z.object({
    details: VehicleDetailsSchema.extend({
        engineCC: z.coerce
            .number({ message: 'Required' })
            .min(1, { message: 'Required' }),
        adjustableSeats: z.boolean().nullable(),
        helmet: z.boolean().nullable(),
        gloves: z.boolean().nullable(),
    }),
});

export const VehiclePartialCarAmenitiesSchema = z.object({
    details: VehicleCarAmenitiesSchema.shape.details
        .extend({
            make: z.string(),
            model: z.string(),
            numberOfDoors: z.coerce.number(),
            numberOfSeats: z.coerce.number(),
            bodyType: z.string(),
        })
        .partial(),
});

export const VehiclePartialMotorbikeAmenitiesSchema = z.object({
    details: VehicleMotorbikeAmenitiesSchema.shape.details
        .extend({
            make: z.string(),
            model: z.string(),
            engineCC: z.coerce.number(),
        })
        .partial(),
});

export const ActivityAmenitiesSchema = z.object({
    details: z.object({
        quantity: z.coerce.number(),
    }),
});

const BaseBoatAmenitiesSchema = z.object({
    details: z.object({
        bedrooms: z.number().min(1, { message: 'At least 1 bedroom required' }),
        beds: z.number().min(1, { message: 'At least 1 bed required' }),
        baths: z.number().min(1, { message: 'At least 1 bathroom required' }),
        maxGuests: z.number().min(1, { message: 'At least 1 guest required ' }),
        overnightGuests: z.number(),
        catering: z.boolean().default(false),
        alcohol: z.boolean().default(false),
        wifi: z.boolean().default(false),
        boardroom: z.boolean().default(false),
        tv: z.boolean().default(false),
        kitchen: z.boolean().default(false),
        aircon: z.boolean().default(false),
        bbq: z.boolean().default(false),
        spa: z.boolean().default(false),
        waterSports: z.boolean().default(false),
    }),
    spaces: z.array(SpaceSchema.and(z.object({ id: z.number().optional() }))),
});

export const BoatAmenitiesSchema = BaseBoatAmenitiesSchema.superRefine(
    (val, ctx) => {
        const totalSleepingCapacity = val.spaces.reduce((acc, space) => {
            if (space.spaceType !== SpaceType.Bedroom) return acc;
            const cap = space.sleepingCapacity;
            return acc + (cap ?? 0);
        }, 0);
        const overnightGuests = val.details.overnightGuests;
        if (totalSleepingCapacity > overnightGuests) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    'Total bedroom sleeping capacity cannot exceed overnight guests.',
                path: ['details', 'overnightGuests'],
            });
        }
    },
);

export const BoatPartialAmenitiesSchema = BaseBoatAmenitiesSchema.extend({
    details: BaseBoatAmenitiesSchema.shape.details
        .extend({
            bedrooms: z.number(),
            beds: z.number(),
            baths: z.number(),
            maxGuests: z.number(),
        })
        .partial(),
});

export const getAmenitiesSchema = (experience: Experience) => {
    switch (experience.type) {
        case ExperienceType.Activity:
            return ActivityAmenitiesSchema;
        case ExperienceType.Boat:
            return BoatAmenitiesSchema;
        case ExperienceType.Commercial:
            return CommercialAmenitiesSchema;
        case ExperienceType.Property:
            return PropertyAmenitiesSchema;
        case ExperienceType.Vehicle:
            return experience.details.subType === 'car'
                ? VehicleCarAmenitiesSchema
                : VehicleMotorbikeAmenitiesSchema;
    }
};

export const getAmenitiesPartialSchema = (experience: Experience) => {
    switch (experience.type) {
        case ExperienceType.Activity:
            return ActivityAmenitiesSchema;
        case ExperienceType.Boat:
            return BoatPartialAmenitiesSchema;
        case ExperienceType.Commercial:
            return CommercialPartialAmenitiesSchema;
        case ExperienceType.Property:
            return PropertyPartialAmenitiesSchema;
        case ExperienceType.Vehicle:
            return experience.details.subType === 'car'
                ? VehiclePartialCarAmenitiesSchema
                : VehiclePartialMotorbikeAmenitiesSchema;
    }
};
